/* eslint-disable @typescript-eslint/ban-types */
'use client';
import { Organizations } from '@payments-checkout/shared/helpers/index';
import { analiticaInstance as analiticaInstanceConfigs } from '@payments-checkout/shared/plugins/analitica/analitica.configs';
import { analiticaInstance } from '@payments-checkout/shared/plugins/analitica/analitica.events';
import { PaymentMethodsType } from '@payments-checkout/shared/plugins/analitica/analitica.types';
import { usePaymentMethod, useSessionId } from '@payments-checkout/shared/providers';
import { Skeleton, Text } from '@stone-payments/jade';
import { useParams } from 'next/navigation';
import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useAutoPreselectPaymentMethod } from '../../hooks/useAutoPreselectPaymentMethod';
import { usePageSharedData } from '../../hooks/usePageSharedData';
import { usePaymentLinkId } from '../../hooks/usePaymentLinkId';
import { usePreselectVariant } from '../../hooks/usePreselectVariant';
import { acceptedPaymentMethod } from '../../types/customer.types';
import { type PaymentSettings } from '../../types/paymentLink.types';
import { PaymentMethodCard } from '../paymentMethodCard';
import {
  PaymentMethodSelectionContainer,
  PaymentMethodSelectionContent,
} from './paymentMethodSelection.styles';

export function PaymentMethodSelection({
  paymentSettings,
}: {
  paymentSettings: PaymentSettings;
}) {
  const {
    setPaymentMethod,
    paymentMethod: userPaymentMethod,
    setSelectedDigitalWallet,
    setPaymentWithClickToPay,
    isCustomerFormLoading,
  } = usePaymentMethod();
  const { isLoading, skipPreselect } = usePreselectVariant();
  useAutoPreselectPaymentMethod(paymentSettings);
  const { hasAnyDigitalWallet } = usePageSharedData();
  const params = useParams<{ paymentLinkId: string }>();
  const { data } = usePaymentLinkId(params?.paymentLinkId);
  const organization =
    data?.account_settings?.organization || Organizations.PAGARME;
  const { formatMessage } = useIntl();
  const { sessionId } = useSessionId();

  const amplitudeIdentifier = useMemo(() => {
    const stored =
        sessionStorage.getItem('amplitudeIdentifier')
    return stored || sessionId
  }, [sessionId]);

  const renderablePaymentMethods: acceptedPaymentMethod[] = [
    'credit_card',
    'pix',
    'boleto',
  ];

  useEffect(() => {
    analiticaInstanceConfigs.updateMetadata((actualMetadata) => ({
      ...actualMetadata,
      project: organization,
      paymentLinkId: params?.paymentLinkId,
      user: {
        id: amplitudeIdentifier
      }
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amplitudeIdentifier]);

  const maxInstallments = (paymentMethod: acceptedPaymentMethod) => {
    if (paymentMethod !== 'credit_card') {
      return undefined;
    }

    const installments = paymentSettings.credit_card_settings?.installments;
    if (!installments?.length) {
      return undefined;
    }

    return Math.max(...installments.map(({ number }) => number));
  };

  const hasDiscount = (paymentMethod: acceptedPaymentMethod) => {
    if (!paymentMethod || paymentMethod === 'debit_card') {
      return false;
    }

    switch (paymentMethod) {
      case 'credit_card':
        return (
          !!paymentSettings.credit_card_settings?.discount ||
          !!paymentSettings.credit_card_settings?.discount_percentage
        );
      case 'pix':
        return (
          !!paymentSettings.pix_settings?.discount ||
          !!paymentSettings.pix_settings?.discount_percentage
        );
      case 'boleto':
        return (
          !!paymentSettings.boleto_settings?.discount ||
          !!paymentSettings.boleto_settings?.discount_percentage
        );
      default:
        return false;
    }
  };

  const selectPaymentType = (paymentMethod: acceptedPaymentMethod) => {
    if (isCustomerFormLoading) return;
    if (paymentMethod) {
      setPaymentMethod(paymentMethod);
      setSelectedDigitalWallet(null);
      hideClickToPayMessage()
      setPaymentWithClickToPay(false);
      const paymentMethodPossibleValues = {
        credit_card: 'credito',
        pix: 'pix',
        boleto: 'boleto',
      };

      if (paymentMethod !== 'debit_card') {
        analiticaInstance.events.homePage.paymentMethodClicked(
          paymentMethodPossibleValues[paymentMethod] as PaymentMethodsType
        );
      }
    }
  };

  const hideClickToPayMessage = () => {
    const clickToPayMessage = document.getElementById('hst_disclaimer_span');
    if (clickToPayMessage) {
      clickToPayMessage.style.display = 'none'
    }
  }

  const renderPaymentMethod = (
    paymentMethod: acceptedPaymentMethod,
    index: number
  ) => {
    if (!renderablePaymentMethods.includes(paymentMethod)) {
      return null;
    }

    return (
      <PaymentMethodSelectionContent
        index={index}
        acceptedPaymentMethods={
          paymentSettings.accepted_payment_methods as string[]
        }
        key={paymentMethod}
      >
        {isLoading ? (
          <Skeleton height="52px" cornerRadius="medium" />
        ) : (
          <PaymentMethodCard
            paymentType={paymentMethod as 'credit_card' | 'pix' | 'boleto'}
            isSelected={paymentMethod === userPaymentMethod}
            selectPaymentMethod={selectPaymentType}
            hasDiscount={hasDiscount(paymentMethod) || false}
            maxInstallments={maxInstallments(paymentMethod)}
            disabled={isCustomerFormLoading}
          />
        )}
      </PaymentMethodSelectionContent>
    );
  };

  const shouldShowTitle = !hasAnyDigitalWallet && !skipPreselect && !isLoading;
  const shouldShowTitleSkeleton = !hasAnyDigitalWallet && isLoading;

  return (
    <div className="flex gap-jade-300 flex-col">
      {shouldShowTitleSkeleton && (
        <Skeleton height="24px" width="200px" cornerRadius="medium" />
      )}
      {shouldShowTitle && (
        <Text variant="heading-small">
          {formatMessage({ id: 'paymentMethod.form.title' })}
        </Text>
      )}
      <PaymentMethodSelectionContainer data-testid="checkout-payment-method-selection">
        {paymentSettings.accepted_payment_methods.map(renderPaymentMethod)}
      </PaymentMethodSelectionContainer>
    </div>
  );
}